import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import apis from '../../../../modules/apis'
import { useAITutorPermission } from '../../../../modules/apis/student/ai-tutor'
import { usePromotionCode } from '../../../../modules/apis/student/promotion'
import { axiosGet } from '../../../../modules/axiosHelper'
import { PRICING_CURRENCY_OPTIONS } from '../../../../modules/consts/pricing'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { useLocaleVars } from '../../../../modules/i18n/useTranslation'
import { AI_TUTOR_COURSE, COUNTRY_ALPHA2_CODE } from '../../../../modules/vars'

const ProductContext = createContext(null)
export const useProductContext = () => useContext(ProductContext)

export enum productTypeEnum {
  RINGLE_PACKAGE,
  PERSONAL_LESSON,
  AI_TUTOR,
}

const fetchB2CProducts = async (payload) => {
  const query = new URLSearchParams(payload).toString()
  const response = await axiosGet(`${apis.products.lines_b2b()}?${query}`)
  return response
}

const fetchB2B2CProducts = async (payload) => {
  const query = new URLSearchParams(payload).toString()
  const response = await axiosGet(`${apis.products.b2b2c()}?${query}`)
  return response
}

export const useFetchB2CProducts = (params) => {
  const payload = {
    product_line_type: params.productType,
    duration: params.duration,
    currency: params.currency || 'KRW',
    locale: params.locale || 'ko',
  }

  return useQuery({
    queryKey: ['b2c-products', params.productType, params.duration, params.currency || 'KRW', params.locale || 'ko'],
    queryFn: () => fetchB2CProducts(payload),
    enabled: !!params.productType,
  })
}

export const useFetchB2B2CProducts = (params) => {
  const payload = {
    duration: params.duration,
    currency: params.currency || 'KRW',
    locale: params.locale || 'ko',
  }

  return useQuery({
    queryKey: ['b2b2c-products', params.duration, params.currency || 'KRW', params.locale || 'ko'],
    queryFn: () => fetchB2B2CProducts(payload),
    enabled: !!params.duration,
  })
}

const PRODUCT_TYPE = {
  ONE_ON_ONE: '1on1',
  RINGLE_PACKAGE: 'combined',
  AI_TUTOR: 'aitutor',
} as const
type ProductType = (typeof PRODUCT_TYPE)[keyof typeof PRODUCT_TYPE]

const PRODUCT_URL_QUERY_TYPE = {
  [productTypeEnum.RINGLE_PACKAGE]: 'combined',
  [productTypeEnum.PERSONAL_LESSON]: '1on1',
  [productTypeEnum.AI_TUTOR]: 'ai-tutor',
} as const

export const ProductProvider = ({ children }) => {
  const scrollTargetRef = useRef<HTMLButtonElement>(null)

  const router = useAppRouter()
  const { lang, product } = router.query

  const { duration_list } = useLocaleVars()
  const { currentUser } = useCurrentUser()
  const { data: promotionData } = usePromotionCode({ enabled: !!currentUser })
  const applicablePromotionId = promotionData?.promotion_id
  const { data: aiTutorMembershipPermissionData } = useAITutorPermission(AI_TUTOR_COURSE, {
    enabled: !!currentUser,
  })

  const [productType, setProductType] = useState({
    id: 24,
    type: productTypeEnum.RINGLE_PACKAGE,
  })

  const [purchaseInfo, setPurchaseInfo] = useState({
    id: null,
    duration: duration_list[0],
    countryCode: COUNTRY_ALPHA2_CODE.KR,
  })

  const convertedCountryCode =
    purchaseInfo?.countryCode == COUNTRY_ALPHA2_CODE.KR ? purchaseInfo.countryCode : COUNTRY_ALPHA2_CODE.US

  const productsPayload = {
    productType: productType.id,
    duration: purchaseInfo.duration.number,
    currency: PRICING_CURRENCY_OPTIONS.find((option) => option.country_code === purchaseInfo.countryCode)?.currency,
    locale: lang,
  }

  const hasAiTutorMembership =
    !!aiTutorMembershipPermissionData?.temp_membership || aiTutorMembershipPermissionData?.valid_memberships?.length > 0

  const scrollToTarget = () => {
    if (scrollTargetRef.current) {
      scrollTargetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }

  useEffect(() => {
    if (!product) return

    const validProductTypes = Object.values(PRODUCT_TYPE) as ProductType[]
    const productString = Array.isArray(product) ? product[0] : product

    if (!validProductTypes.includes(productString as ProductType)) {
      const currentPath = router.asPath
      const targetPath = `/${lang}/1on1/pricing` // 모든 유효하지 않은 값은 1on1으로 리다이렉트

      if (currentPath !== targetPath) {
        router.replace(targetPath, undefined, { shallow: true })
      }
      return
    }

    setProductType(
      (product as ProductType) === PRODUCT_TYPE.AI_TUTOR
        ? { id: null, type: productTypeEnum.AI_TUTOR }
        : { id: 24, type: productTypeEnum.RINGLE_PACKAGE } // 1on1과 combined 모두 기본값으로 RINGLE_PACKAGE
    )
  }, [product, lang])

  // url query에 따라 productType을 설정 (product 파라미터가 없을 때만)
  useEffect(() => {
    if (product) return // product 파라미터가 있으면 건너뛰기

    const productParam = router.query?.type || ''

    switch (productParam) {
      case PRODUCT_URL_QUERY_TYPE[productTypeEnum.AI_TUTOR]:
        setProductType({ id: null, type: productTypeEnum.AI_TUTOR })
        break
      case PRODUCT_URL_QUERY_TYPE[productTypeEnum.PERSONAL_LESSON]:
        setProductType({ id: 19, type: productTypeEnum.PERSONAL_LESSON })
        break
      case PRODUCT_URL_QUERY_TYPE[productTypeEnum.RINGLE_PACKAGE]:
        setProductType({ id: 24, type: productTypeEnum.RINGLE_PACKAGE })
        break
      default:
        setProductType({ id: 24, type: productTypeEnum.RINGLE_PACKAGE })
        break
    }
  }, [router.query?.type, product])

  // productType이 변경될 때마다 url query를 변경
  useEffect(() => {
    if (!productType) return

    const typeQuery = () => {
      switch (productType.type) {
        case productTypeEnum.RINGLE_PACKAGE:
          return PRODUCT_URL_QUERY_TYPE[productTypeEnum.RINGLE_PACKAGE]
        case productTypeEnum.PERSONAL_LESSON:
          return PRODUCT_URL_QUERY_TYPE[productTypeEnum.PERSONAL_LESSON]
        case productTypeEnum.AI_TUTOR:
          return PRODUCT_URL_QUERY_TYPE[productTypeEnum.AI_TUTOR]
        default:
          return PRODUCT_URL_QUERY_TYPE[productTypeEnum.PERSONAL_LESSON]
      }
    }

    router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, type: typeQuery() },
      },
      undefined,
      { shallow: true }
    )
  }, [productType])

  return (
    <ProductContext.Provider
      value={{
        scrollTargetRef,
        scrollToTarget,
        productsPayload,
        purchaseInfo,
        setPurchaseInfo,
        productType,
        setProductType,
        convertedCountryCode,
        applicablePromotionId,
        hasAiTutorMembership,
      }}
    >
      {children}
    </ProductContext.Provider>
  )
}
